import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import AuthService from "../AuthService";

const tabs = [
  { key: "resources", label: "Learning Resources", icon: "bi-collection-play" },
  { key: "live", label: "Live Classes", icon: "bi-camera-video" },
  { key: "categories", label: "Categories", icon: "bi-tags" },
];

const emptyCategory = {
  id: null,
  name: "",
  code: "",
  description: "",
  is_active: true,
};

const emptyResource = {
  id: null,
  title: "",
  category: "",
  academic_year: "",
  term: "",
  class_group: "",
  class_subject: "",
  teacher: "",
  resource_type: "NOTE",
  description: "",
  content: "",
  external_url: "",
  access_level: "PAID_ONLY",
  preview_text: "",
  status: "DRAFT",
  publish_date: "",
  due_date: "",
  allow_download: true,
  audience_class_groups: [],
};

const emptyLiveClass = {
  id: null,
  title: "",
  academic_year: "",
  term: "",
  class_group: "",
  class_subject: "",
  teacher: "",
  platform: "GOOGLE_MEET",
  meeting_url: "",
  scheduled_start: "",
  scheduled_end: "",
  duration_minutes: "60",
  status: "SCHEDULED",
  access_level: "PAID_ONLY",
  description: "",
  recording_url: "",
  notes: "",
  audience_class_groups: [],
};

function Elearning() {
  const currentUser = AuthService.getCurrentUser();
  const isTeacher = currentUser?.user_type === "TEACHER";
  const [activeTab, setActiveTab] = useState("resources");
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({});
  const [categories, setCategories] = useState([]);
  const [resources, setResources] = useState([]);
  const [liveClasses, setLiveClasses] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [terms, setTerms] = useState([]);
  const [classGroups, setClassGroups] = useState([]);
  const [classSubjects, setClassSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [teacherAllocations, setTeacherAllocations] = useState([]);
  const [categoryForm, setCategoryForm] = useState(emptyCategory);
  const [resourceForm, setResourceForm] = useState(() => ({
    ...emptyResource,
    teacher: isTeacher ? currentUser?.id || "" : "",
  }));
  const [liveForm, setLiveForm] = useState(() => ({
    ...emptyLiveClass,
    teacher: isTeacher ? currentUser?.id || "" : "",
  }));
  const [resourceFile, setResourceFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [search, setSearch] = useState("");

  const teacherAllowedClassSubjectIds = useMemo(() => {
    if (!isTeacher) return null;

    return new Set(
      teacherAllocations
        .filter(
          (allocation) =>
            String(allocation.teacher) === String(currentUser?.id) &&
            allocation.is_active !== false
        )
        .map((allocation) => String(allocation.class_subject))
    );
  }, [isTeacher, teacherAllocations, currentUser?.id]);

  const availableClassSubjects = useMemo(() => {
    if (!isTeacher || !teacherAllowedClassSubjectIds) return classSubjects;
    return classSubjects.filter((item) => teacherAllowedClassSubjectIds.has(String(item.id)));
  }, [classSubjects, isTeacher, teacherAllowedClassSubjectIds]);

  const teacherOptionsForClassSubject = (classSubjectId) => {
    if (!classSubjectId) return teachers;

    const allowedTeacherIds = new Set(
      teacherAllocations
        .filter(
          (allocation) =>
            String(allocation.class_subject) === String(classSubjectId) &&
            allocation.is_active !== false
        )
        .map((allocation) => String(allocation.teacher))
    );

    const allowedTeachers = teachers.filter((teacher) => allowedTeacherIds.has(String(teacher.id)));
    return allowedTeachers.length > 0 ? allowedTeachers : teachers;
  };

  const filteredClassSubjects = useMemo(() => {
    if (!resourceForm.class_group) return availableClassSubjects;
    return availableClassSubjects.filter((item) => String(item.class_group) === String(resourceForm.class_group));
  }, [availableClassSubjects, resourceForm.class_group]);

  const filteredLiveClassSubjects = useMemo(() => {
    if (!liveForm.class_group) return availableClassSubjects;
    return availableClassSubjects.filter((item) => String(item.class_group) === String(liveForm.class_group));
  }, [availableClassSubjects, liveForm.class_group]);

  const visibleResources = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return resources;
    return resources.filter((item) =>
      [item.title, item.subject_name, item.class_group_name, item.teacher_name, item.resource_type]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [resources, search]);

  const loadAll = async () => {
    try {
      setLoading(true);
      await axiosInstance.post("elearning/bootstrap/");
      const [
        summaryResponse,
        categoriesResponse,
        resourcesResponse,
        liveClassesResponse,
        yearsResponse,
        termsResponse,
        groupsResponse,
        subjectsResponse,
        teachersResponse,
        teacherAllocationsResponse,
      ] = await Promise.all([
        axiosInstance.get("elearning/summary/"),
        axiosInstance.get("elearning/categories/"),
        axiosInstance.get("elearning/resources/"),
        axiosInstance.get("elearning/live-classes/"),
        axiosInstance.get("academic/years/"),
        axiosInstance.get("academic/terms/"),
        axiosInstance.get("academic/class-groups/"),
        axiosInstance.get("exams/class-subjects/"),
        axiosInstance.get("academic/setup/teachers/"),
        axiosInstance.get("academic/teacher-allocations/"),
      ]);

      setSummary(summaryResponse.data || {});
      setCategories(categoriesResponse.data || []);
      setResources(resourcesResponse.data || []);
      setLiveClasses(liveClassesResponse.data || []);
      setAcademicYears(yearsResponse.data || []);
      setTerms(termsResponse.data || []);
      setClassGroups(groupsResponse.data || []);
      setClassSubjects(subjectsResponse.data || []);
      setTeachers(teachersResponse.data || []);
      setTeacherAllocations(teacherAllocationsResponse.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load e-learning.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const saveCategory = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      if (categoryForm.id) {
        await axiosInstance.patch(`elearning/categories/${categoryForm.id}/`, categoryForm);
        toast.success("Category updated.");
      } else {
        await axiosInstance.post("elearning/categories/", categoryForm);
        toast.success("Category added.");
      }
      setCategoryForm(emptyCategory);
      await loadAll();
    } catch (error) {
      showError(error, "Failed to save category.");
    } finally {
      setLoading(false);
    }
  };

  const saveResource = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const payload = new FormData();
      Object.entries(resourceForm).forEach(([key, value]) => {
        if (key === "audience_class_groups") {
          (value || []).forEach((classGroupId) => payload.append("audience_class_groups", classGroupId));
        } else if (key !== "id" && value !== "" && value !== null && value !== undefined) {
          payload.append(key, value);
        }
      });
      if (resourceFile) payload.append("file", resourceFile);
      if (thumbnailFile) payload.append("thumbnail", thumbnailFile);

      const config = { headers: { "Content-Type": "multipart/form-data" } };
      if (resourceForm.id) {
        await axiosInstance.patch(`elearning/resources/${resourceForm.id}/`, payload, config);
        toast.success("Learning resource updated.");
      } else {
        await axiosInstance.post("elearning/resources/", payload, config);
        toast.success("Learning resource added.");
      }

      setResourceForm({ ...emptyResource, teacher: isTeacher ? currentUser?.id || "" : "" });
      setResourceFile(null);
      setThumbnailFile(null);
      await loadAll();
    } catch (error) {
      showError(error, "Failed to save learning resource.");
    } finally {
      setLoading(false);
    }
  };

  const editResource = (resource) => {
    setResourceForm({
      id: resource.id,
      title: resource.title || "",
      category: resource.category || "",
      academic_year: resource.academic_year || "",
      term: resource.term || "",
      class_group: resource.class_group || "",
      class_subject: resource.class_subject || "",
      teacher: resource.teacher || "",
      resource_type: resource.resource_type || "NOTE",
      description: resource.description || "",
      content: resource.content || "",
      external_url: resource.external_url || "",
      access_level: resource.access_level || "PAID_ONLY",
      preview_text: resource.preview_text || "",
      status: resource.status || "DRAFT",
      publish_date: resource.publish_date || "",
      due_date: resource.due_date || "",
      allow_download: Boolean(resource.allow_download),
      audience_class_groups: resource.audience_class_groups || [],
    });
    setResourceFile(null);
    setThumbnailFile(null);
    setActiveTab("resources");
  };

  const deleteRecord = async (url, label) => {
    if (!window.confirm(`Delete ${label}?`)) return;
    try {
      setLoading(true);
      await axiosInstance.delete(url);
      toast.success(`${label} deleted.`);
      await loadAll();
    } catch (error) {
      toast.error(`Could not delete ${label}. It may already be in use.`);
    } finally {
      setLoading(false);
    }
  };

  const saveLiveClass = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const payload = new FormData();
      Object.entries(liveForm).forEach(([key, value]) => {
        if (key === "audience_class_groups") {
          (value || []).forEach((classGroupId) => payload.append("audience_class_groups", classGroupId));
        } else if (key !== "id" && value !== "" && value !== null && value !== undefined) {
          payload.append(key, value);
        }
      });

      if (liveForm.id) {
        await axiosInstance.patch(`elearning/live-classes/${liveForm.id}/`, payload);
        toast.success("Live class updated.");
      } else {
        await axiosInstance.post("elearning/live-classes/", payload);
        toast.success("Live class scheduled.");
      }

      setLiveForm({ ...emptyLiveClass, teacher: isTeacher ? currentUser?.id || "" : "" });
      await loadAll();
    } catch (error) {
      showError(error, "Failed to save live class.");
    } finally {
      setLoading(false);
    }
  };

  const editLiveClass = (liveClass) => {
    setLiveForm({
      id: liveClass.id,
      title: liveClass.title || "",
      academic_year: liveClass.academic_year || "",
      term: liveClass.term || "",
      class_group: liveClass.class_group || "",
      class_subject: liveClass.class_subject || "",
      teacher: liveClass.teacher || "",
      platform: liveClass.platform || "GOOGLE_MEET",
      meeting_url: liveClass.meeting_url || "",
      scheduled_start: toDateTimeLocal(liveClass.scheduled_start),
      scheduled_end: toDateTimeLocal(liveClass.scheduled_end),
      duration_minutes: liveClass.duration_minutes || "60",
      status: liveClass.status || "SCHEDULED",
      access_level: liveClass.access_level || "PAID_ONLY",
      description: liveClass.description || "",
      recording_url: liveClass.recording_url || "",
      notes: liveClass.notes || "",
      audience_class_groups: liveClass.audience_class_groups || [],
    });
    setActiveTab("live");
  };

  return (
    <div>
      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <h5 className="fw-bold mb-1">E-learning</h5>
              <p className="text-muted mb-0">
                Publish notes, videos, assignments, and class resources with fee-aware student access.
              </p>
            </div>
            <button className="btn btn-outline-success" onClick={loadAll} disabled={loading}>
              <i className="bi bi-arrow-clockwise me-2"></i>
              Refresh
            </button>
          </div>
          <div className="row g-2 mt-3">
            <Summary label="Resources" value={summary.resources || 0} />
            <Summary label="Published" value={summary.published || 0} />
            <Summary label="Drafts" value={summary.drafts || 0} />
            <Summary label="Live Classes" value={summary.live_classes || 0} />
          </div>
        </div>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`btn btn-sm ${activeTab === tab.key ? "btn-success" : "btn-light border"}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <i className={`bi ${tab.icon} me-2`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "resources" && (
        <SetupCard title="Learning Resources">
          <form className="row g-3 mb-4" onSubmit={saveResource}>
            {isTeacher && (
              <div className="col-12">
                <div className="alert alert-light border mb-0">
                  You are adding this material as {currentUser?.full_name || currentUser?.email || "a teacher"}.
                </div>
              </div>
            )}
            <TextField label="Title" value={resourceForm.title} onChange={(value) => setResourceForm((prev) => ({ ...prev, title: value }))} required />
            <SelectField label="Category" value={resourceForm.category} onChange={(value) => setResourceForm((prev) => ({ ...prev, category: value }))} options={categories.filter((item) => item.is_active).map((item) => [item.id, item.name])} required={false} />
            <SelectField label="Resource Type" value={resourceForm.resource_type} onChange={(value) => setResourceForm((prev) => ({ ...prev, resource_type: value }))} options={[
              ["NOTE", "Notes / Document"],
              ["VIDEO", "Video"],
              ["LINK", "External Link"],
              ["ASSIGNMENT", "Assignment"],
              ["PAST_PAPER", "Past Paper"],
              ["OTHER", "Other"],
            ]} />
            <SelectField label="Academic Year" value={resourceForm.academic_year} onChange={(value) => setResourceForm((prev) => ({ ...prev, academic_year: value }))} options={academicYears.map((item) => [item.id, item.name])} required={false} />
            <SelectField label="Term" value={resourceForm.term} onChange={(value) => setResourceForm((prev) => ({ ...prev, term: value }))} options={terms.map((item) => [item.id, item.academic_year_name ? `${item.academic_year_name} - ${item.name}` : item.name])} required={false} />
            <SelectField label="Class Group" value={resourceForm.class_group} onChange={(value) => setResourceForm((prev) => ({ ...prev, class_group: value, class_subject: "" }))} options={classGroups.map((item) => [item.id, item.display_name || item.name])} />
            <SelectField label="Subject" value={resourceForm.class_subject} onChange={(value) => setResourceForm((prev) => ({ ...prev, class_subject: value, teacher: isTeacher ? currentUser?.id || "" : "" }))} options={filteredClassSubjects.map((item) => [item.id, `${item.class_group_name} - ${item.subject_name}`])} />
            {isTeacher && availableClassSubjects.length === 0 && (
              <div className="col-12">
                <div className="alert alert-warning py-2 mb-0">
                  No active teaching allocation was found for your account. Ask Admin to add your Teacher Allocation first.
                </div>
              </div>
            )}
            <SelectField label="Teacher" value={resourceForm.teacher} onChange={(value) => setResourceForm((prev) => ({ ...prev, teacher: value }))} options={teacherOptionsForClassSubject(resourceForm.class_subject).map((item) => [item.id, item.full_name])} required={false} disabled={isTeacher} />
            <MultiSelectField label="Also Send To" value={resourceForm.audience_class_groups} onChange={(value) => setResourceForm((prev) => ({ ...prev, audience_class_groups: value }))} options={classGroups.map((item) => [item.id, item.display_name || item.name])} />
            <SelectField label="Access Level" value={resourceForm.access_level} onChange={(value) => setResourceForm((prev) => ({ ...prev, access_level: value }))} options={[
              ["ALL_STUDENTS", "All Students"],
              ["PAID_ONLY", "Paid Students Only"],
              ["PREVIEW", "Preview Available"],
            ]} />
            <SelectField label="Status" value={resourceForm.status} onChange={(value) => setResourceForm((prev) => ({ ...prev, status: value }))} options={[
              ["DRAFT", "Draft"],
              ["PUBLISHED", "Published"],
              ["ARCHIVED", "Archived"],
            ]} />
            <TextField label="Publish Date" type="date" value={resourceForm.publish_date || ""} onChange={(value) => setResourceForm((prev) => ({ ...prev, publish_date: value }))} />
            <TextField label="Due Date" type="date" value={resourceForm.due_date || ""} onChange={(value) => setResourceForm((prev) => ({ ...prev, due_date: value }))} />
            <TextField label="External URL" value={resourceForm.external_url || ""} onChange={(value) => setResourceForm((prev) => ({ ...prev, external_url: value }))} placeholder="YouTube, Google Drive, website link" />

            <div className="col-md-4">
              <label className="form-label fw-semibold">Resource File</label>
              <input type="file" className="form-control" onChange={(event) => setResourceFile(event.target.files?.[0] || null)} />
              <small className="text-muted">PDF, Word, image, audio, or video file.</small>
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold">Thumbnail</label>
              <input type="file" className="form-control" accept="image/*" onChange={(event) => setThumbnailFile(event.target.files?.[0] || null)} />
            </div>
            <SwitchField label="Allow download" checked={resourceForm.allow_download} onChange={(value) => setResourceForm((prev) => ({ ...prev, allow_download: value }))} />

            <div className="col-md-6">
              <label className="form-label fw-semibold">Description</label>
              <textarea className="form-control" rows="3" value={resourceForm.description || ""} onChange={(event) => setResourceForm((prev) => ({ ...prev, description: event.target.value }))} />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Preview Text</label>
              <textarea className="form-control" rows="3" value={resourceForm.preview_text || ""} onChange={(event) => setResourceForm((prev) => ({ ...prev, preview_text: event.target.value }))} />
            </div>
            <div className="col-12">
              <label className="form-label fw-semibold">Content / Instructions</label>
              <textarea className="form-control" rows="3" value={resourceForm.content || ""} onChange={(event) => setResourceForm((prev) => ({ ...prev, content: event.target.value }))} />
            </div>
            <SaveBar loading={loading} label={resourceForm.id ? "Update Resource" : "Add Resource"} onCancel={resourceForm.id ? () => setResourceForm({ ...emptyResource, teacher: isTeacher ? currentUser?.id || "" : "" }) : null} />
          </form>

          <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
            <h6 className="fw-bold mb-0">Published and Draft Resources</h6>
            <input className="form-control" style={{ maxWidth: 320 }} placeholder="Search resources..." value={search} onChange={(event) => setSearch(event.target.value)} />
          </div>

          <SimpleTable
            headers={["Title", "Primary Class", "Other Classes", "Subject", "Type", "Access", "Status", "File", "Actions"]}
            rows={visibleResources.map((resource) => [
              <div><div className="fw-semibold">{resource.title}</div><small className="text-muted">{resource.teacher_name || "No teacher selected"}</small></div>,
              resource.class_group_name,
              (resource.audience_class_group_names || []).join(", ") || "-",
              resource.subject_name,
              resource.resource_type,
              accessLabel(resource.access_level),
              <Status value={resource.status} />,
              resource.file_url ? <a href={resource.file_url} target="_blank" rel="noreferrer">Open file</a> : resource.external_url ? <a href={resource.external_url} target="_blank" rel="noreferrer">Open link</a> : "-",
              <ActionButtons onEdit={() => editResource(resource)} onDelete={() => deleteRecord(`elearning/resources/${resource.id}/`, resource.title)} />,
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "live" && (
        <SetupCard title="Live Online Classes">
          <form className="row g-3 mb-4" onSubmit={saveLiveClass}>
            {isTeacher && (
              <div className="col-12">
                <div className="alert alert-light border mb-0">
                  You are scheduling this live lesson as {currentUser?.full_name || currentUser?.email || "a teacher"}.
                </div>
              </div>
            )}
            <TextField label="Title" value={liveForm.title} onChange={(value) => setLiveForm((prev) => ({ ...prev, title: value }))} required />
            <SelectField label="Academic Year" value={liveForm.academic_year} onChange={(value) => setLiveForm((prev) => ({ ...prev, academic_year: value }))} options={academicYears.map((item) => [item.id, item.name])} required={false} />
            <SelectField label="Term" value={liveForm.term} onChange={(value) => setLiveForm((prev) => ({ ...prev, term: value }))} options={terms.map((item) => [item.id, item.academic_year_name ? `${item.academic_year_name} - ${item.name}` : item.name])} required={false} />
            <SelectField label="Primary Class" value={liveForm.class_group} onChange={(value) => setLiveForm((prev) => ({ ...prev, class_group: value, class_subject: "" }))} options={classGroups.map((item) => [item.id, item.display_name || item.name])} />
            <SelectField label="Subject" value={liveForm.class_subject} onChange={(value) => setLiveForm((prev) => ({ ...prev, class_subject: value, teacher: isTeacher ? currentUser?.id || "" : "" }))} options={filteredLiveClassSubjects.map((item) => [item.id, `${item.class_group_name} - ${item.subject_name}`])} />
            <SelectField label="Teacher" value={liveForm.teacher} onChange={(value) => setLiveForm((prev) => ({ ...prev, teacher: value }))} options={teacherOptionsForClassSubject(liveForm.class_subject).map((item) => [item.id, item.full_name])} required={false} disabled={isTeacher} />
            <MultiSelectField label="Also Invite" value={liveForm.audience_class_groups} onChange={(value) => setLiveForm((prev) => ({ ...prev, audience_class_groups: value }))} options={classGroups.map((item) => [item.id, item.display_name || item.name])} />
            <SelectField label="Platform" value={liveForm.platform} onChange={(value) => setLiveForm((prev) => ({ ...prev, platform: value }))} options={[
              ["ZOOM", "Zoom"],
              ["GOOGLE_MEET", "Google Meet"],
              ["MICROSOFT_TEAMS", "Microsoft Teams"],
              ["JITSI", "Jitsi"],
              ["OTHER", "Other"],
            ]} />
            <TextField label="Meeting Link" value={liveForm.meeting_url} onChange={(value) => setLiveForm((prev) => ({ ...prev, meeting_url: value }))} required />
            <TextField label="Start Time" type="datetime-local" value={liveForm.scheduled_start || ""} onChange={(value) => setLiveForm((prev) => ({ ...prev, scheduled_start: value }))} required />
            <TextField label="End Time" type="datetime-local" value={liveForm.scheduled_end || ""} onChange={(value) => setLiveForm((prev) => ({ ...prev, scheduled_end: value }))} />
            <TextField label="Duration Minutes" type="number" value={liveForm.duration_minutes} onChange={(value) => setLiveForm((prev) => ({ ...prev, duration_minutes: value }))} />
            <SelectField label="Access Level" value={liveForm.access_level} onChange={(value) => setLiveForm((prev) => ({ ...prev, access_level: value }))} options={[
              ["ALL_STUDENTS", "All Students"],
              ["PAID_ONLY", "Paid Students Only"],
              ["PREVIEW", "Preview Available"],
            ]} />
            <SelectField label="Status" value={liveForm.status} onChange={(value) => setLiveForm((prev) => ({ ...prev, status: value }))} options={[
              ["SCHEDULED", "Scheduled"],
              ["LIVE", "Live"],
              ["COMPLETED", "Completed"],
              ["CANCELLED", "Cancelled"],
            ]} />
            <TextField label="Recording URL" value={liveForm.recording_url || ""} onChange={(value) => setLiveForm((prev) => ({ ...prev, recording_url: value }))} />
            <div className="col-md-6">
              <label className="form-label fw-semibold">Description</label>
              <textarea className="form-control" rows="3" value={liveForm.description || ""} onChange={(event) => setLiveForm((prev) => ({ ...prev, description: event.target.value }))} />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Notes</label>
              <textarea className="form-control" rows="3" value={liveForm.notes || ""} onChange={(event) => setLiveForm((prev) => ({ ...prev, notes: event.target.value }))} />
            </div>
            <SaveBar loading={loading} label={liveForm.id ? "Update Live Class" : "Schedule Live Class"} onCancel={liveForm.id ? () => setLiveForm({ ...emptyLiveClass, teacher: isTeacher ? currentUser?.id || "" : "" }) : null} />
          </form>

          <SimpleTable
            headers={["Title", "Primary Class", "Other Classes", "Subject", "Teacher", "When", "Platform", "Access", "Status", "Actions"]}
            rows={liveClasses.map((liveClass) => [
              liveClass.title,
              liveClass.class_group_name,
              (liveClass.audience_class_group_names || []).join(", ") || "-",
              liveClass.subject_name,
              liveClass.teacher_name || "-",
              formatDateTime(liveClass.scheduled_start),
              liveClass.platform,
              accessLabel(liveClass.access_level),
              <Status value={liveClass.status} />,
              <ActionButtons onEdit={() => editLiveClass(liveClass)} onDelete={() => deleteRecord(`elearning/live-classes/${liveClass.id}/`, liveClass.title)} />,
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "categories" && (
        <SetupCard title="Learning Categories">
          <form className="row g-3 mb-4" onSubmit={saveCategory}>
            <TextField label="Name" value={categoryForm.name} onChange={(value) => setCategoryForm((prev) => ({ ...prev, name: value }))} required />
            <TextField label="Code" value={categoryForm.code} onChange={(value) => setCategoryForm((prev) => ({ ...prev, code: value.toUpperCase() }))} required />
            <SwitchField label="Active" checked={categoryForm.is_active} onChange={(value) => setCategoryForm((prev) => ({ ...prev, is_active: value }))} />
            <div className="col-12">
              <input className="form-control" placeholder="Description" value={categoryForm.description || ""} onChange={(event) => setCategoryForm((prev) => ({ ...prev, description: event.target.value }))} />
            </div>
            <SaveBar loading={loading} label={categoryForm.id ? "Update Category" : "Add Category"} onCancel={categoryForm.id ? () => setCategoryForm(emptyCategory) : null} />
          </form>

          <SimpleTable
            headers={["Name", "Code", "Status", "Actions"]}
            rows={categories.map((category) => [
              category.name,
              category.code,
              <Status value={category.is_active ? "ACTIVE" : "INACTIVE"} />,
              <ActionButtons onEdit={() => setCategoryForm(category)} onDelete={() => deleteRecord(`elearning/categories/${category.id}/`, category.name)} />,
            ])}
          />
        </SetupCard>
      )}
    </div>
  );
}

function accessLabel(value) {
  if (value === "ALL_STUDENTS") return "All Students";
  if (value === "PREVIEW") return "Preview Available";
  return "Paid Students Only";
}

function toDateTimeLocal(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60000);
  return localDate.toISOString().slice(0, 16);
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function showError(error, fallback) {
  const data = error.response?.data;
  const message = data?.detail || Object.values(data || {})?.flat?.()?.[0] || fallback;
  toast.error(message);
}

function Summary({ label, value }) {
  return (
    <div className="col-6 col-xl-3">
      <div className="border rounded-3 p-3 bg-light h-100">
        <small className="text-muted fw-bold">{label}</small>
        <div className="fs-5 fw-bold" style={{ color: "#125b2d" }}>{value}</div>
      </div>
    </div>
  );
}

function SetupCard({ title, children }) {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header bg-white"><h6 className="fw-bold mb-0">{title}</h6></div>
      <div className="card-body">{children}</div>
    </div>
  );
}

function TextField({ label, value, onChange, placeholder = "", type = "text", required = false }) {
  return (
    <div className="col-md-4">
      <label className="form-label fw-semibold">{label}</label>
      <input type={type} className="form-control" placeholder={placeholder} value={value} onChange={(event) => onChange(event.target.value)} required={required} />
    </div>
  );
}

function SelectField({ label, value, onChange, options, required = true, disabled = false }) {
  return (
    <div className="col-md-4">
      <label className="form-label fw-semibold">{label}</label>
      <select className="form-select" value={value || ""} onChange={(event) => onChange(event.target.value)} required={required} disabled={disabled}>
        <option value="">Select {label.toLowerCase()}</option>
        {options.map(([optionValue, optionLabel]) => <option key={optionValue} value={optionValue}>{optionLabel}</option>)}
      </select>
    </div>
  );
}

function MultiSelectField({ label, value, onChange, options }) {
  return (
    <div className="col-md-4">
      <label className="form-label fw-semibold">{label}</label>
      <select
        multiple
        className="form-select"
        value={(value || []).map((item) => String(item))}
        onChange={(event) => {
          const selected = Array.from(event.target.selectedOptions).map((option) => option.value);
          onChange(selected);
        }}
        style={{ minHeight: 96 }}
      >
        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue} value={optionValue}>{optionLabel}</option>
        ))}
      </select>
      <small className="text-muted">Hold Ctrl or Cmd to choose more than one class.</small>
    </div>
  );
}

function SwitchField({ label, checked, onChange }) {
  return (
    <div className="col-md-4 d-flex align-items-end">
      <label className="form-check form-switch border rounded-3 p-3 w-100 mb-0">
        <input type="checkbox" className="form-check-input ms-0 me-2" checked={Boolean(checked)} onChange={(event) => onChange(event.target.checked)} />
        <span className="form-check-label fw-semibold">{label}</span>
      </label>
    </div>
  );
}

function SaveBar({ loading, label, onCancel }) {
  return (
    <div className="col-12 d-flex flex-wrap gap-2 mt-3">
      <button className="btn btn-success fw-semibold" disabled={loading}>{loading ? "Saving..." : label}</button>
      {onCancel && <button type="button" className="btn btn-light border fw-semibold" onClick={onCancel}>Cancel Edit</button>}
    </div>
  );
}

function Status({ value }) {
  const normalized = String(value || "").toUpperCase();
  const className = ["ACTIVE", "PUBLISHED"].includes(normalized)
    ? "bg-success"
    : normalized === "DRAFT"
      ? "bg-warning text-dark"
      : "bg-secondary";
  return <span className={`badge ${className}`}>{value || "-"}</span>;
}

function ActionButtons({ onEdit, onDelete }) {
  return (
    <div className="d-flex align-items-center gap-1">
      <button type="button" className="btn btn-sm btn-light border" title="Edit" onClick={onEdit}><i className="bi bi-pencil-square text-success"></i></button>
      <button type="button" className="btn btn-sm btn-light border" title="Delete" onClick={onDelete}><i className="bi bi-trash text-danger"></i></button>
    </div>
  );
}

function SimpleTable({ headers, rows }) {
  return (
    <div className="table-responsive">
      <table className="table table-hover align-middle mb-0">
        <thead className="table-light"><tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr></thead>
        <tbody>
          {rows.length ? rows.map((row, index) => (
            <tr key={index}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>
          )) : (
            <tr><td colSpan={headers.length} className="text-center text-muted py-4">No records found.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default Elearning;
